import React from 'react';
import {Accordion, ThumbnailGrid} from '../../components';
import {useQuery} from '@apollo/react-hooks';
import {RECENTLY_ADDED_QUERY} from './graphql/RecentlyAddedQuery';
import {
  recentlyAdded,
  recentlyAddedVariables,
} from './graphql/types/recentlyAdded';
import {useHistory} from 'react-router';
import {Button} from '../../../..';

interface Item {
  id: string;
  name: string;
  artistName: string | null;
  image?: string;
  resourceType:
    | 'catalog-album'
    | 'library-album'
    | 'catalog-playlist'
    | 'library-playlist';
  onClick(): void;
}

interface GroupedDates {
  today: Item[];
  yesterday: Item[];
  week: Item[];
  month: Item[];
  year: Item[];
  overYear: Item[];
}

export default function RecentlyAdded() {
  const {data: queryData, fetchMore, loading} = useQuery<
    recentlyAdded,
    recentlyAddedVariables
  >(RECENTLY_ADDED_QUERY, {
    onCompleted: (data) => {
      data.me?.library?.recentlyAdded &&
        localStorage.setItem('recentlyAdded', JSON.stringify(data));
    },
  });

  const storedData = localStorage.getItem('recentlyAdded');
  const data: recentlyAdded | undefined = storedData
    ? JSON.parse(storedData)
    : undefined;

  const history = useHistory();

  const handleLoadMore = () => {
    if (queryData?.me?.library?.recentlyAdded) {
      fetchMore({
        variables: {
          offset: queryData.me.library.recentlyAdded.length,
        },
        updateQuery: (prev, {fetchMoreResult}) => {
          if (!fetchMoreResult) return prev;
          console.log(fetchMoreResult);
          const newLibrary = Object.assign({}, prev.me?.library, {
            recentlyAdded: [
              ...(data?.me?.library?.recentlyAdded || []),
              ...(fetchMoreResult.me?.library?.recentlyAdded || []),
            ],
          });
          const newMe = Object.assign({}, prev.me, {library: newLibrary});
          return Object.assign({}, prev, {
            me: newMe,
          });
        },
      });
    }
  };

  const addedItems = data?.me?.library?.recentlyAdded;

  const groupedDate: GroupedDates = {
    today: [],
    yesterday: [],
    week: [],
    month: [],
    year: [],
    overYear: [],
  };

  const groupedYear: Item[][] = [];

  const today = new Date().setHours(0, 0, 0, 0);
  const year = new Date().getFullYear();
  const msInDay = 24 * 60 * 60 * 1000;

  addedItems?.forEach((item) => {
    const artistName =
      item.__typename === 'LibraryAlbum' ? item.artistName : item.curatorName;

    const onClick =
      item.__typename === 'LibraryAlbum'
        ? () => {
            history.push(`/library/album/${item.id}`);
          }
        : () => {
            history.push(`/library/playlist/${item.id}`);
          };

    const dateAdded = new Date(item.dateAdded).setHours(0, 0, 0, 0);
    const dateAddedYear = new Date(item.dateAdded).getFullYear();

    const daysAgo = (today - dateAdded) / msInDay;

    const formattedItem: Item = {
      id: item.id,
      name: item.name,
      image: item.artwork?.mediumUrl,
      artistName: artistName,
      resourceType:
        item.__typename === 'LibraryAlbum'
          ? 'library-album'
          : 'library-playlist',
      onClick: onClick,
    };

    if (year === dateAddedYear) {
      if (daysAgo === 0) {
        groupedDate.today.push(formattedItem);
      } else if (daysAgo === 1) {
        groupedDate.yesterday.push(formattedItem);
      } else if (daysAgo > 1 && daysAgo <= 7) {
        groupedDate.week.push(formattedItem);
      } else if (daysAgo > 7 && daysAgo <= 30) {
        groupedDate.month.push(formattedItem);
      } else if (daysAgo > 30 && daysAgo <= 365) {
        groupedDate.year.push(formattedItem);
      } else {
        console.log(daysAgo, formattedItem);
        groupedDate.overYear.push(formattedItem);
      }
    } else {
      if (!groupedYear[year - dateAddedYear]) {
        groupedYear[year - dateAddedYear] = [];
      }
      groupedYear[year - dateAddedYear].push(formattedItem);
    }
  });

  const sections: {title: string; content: React.ReactNode}[] = [];

  if (groupedDate.today.length !== 0) {
    sections.push({
      title: 'Today',
      content: <ThumbnailGrid items={groupedDate.today} />,
    });
  }
  if (groupedDate.yesterday.length !== 0) {
    sections.push({
      title: 'Yesterday',
      content: <ThumbnailGrid items={groupedDate.yesterday} />,
    });
  }
  if (groupedDate.week.length !== 0) {
    sections.push({
      title: 'This Week',
      content: <ThumbnailGrid items={groupedDate.week} />,
    });
  }
  if (groupedDate.month.length !== 0) {
    sections.push({
      title: 'This Month',
      content: <ThumbnailGrid items={groupedDate.month} />,
    });
  }
  if (groupedDate.year.length !== 0) {
    sections.push({
      title: 'This Year',
      content: <ThumbnailGrid items={groupedDate.year} />,
    });
  }
  if (groupedDate.overYear.length !== 0) {
    sections.push({
      title: 'Over Year',
      content: <ThumbnailGrid items={groupedDate.overYear} />,
    });
  }

  groupedYear.forEach((items, index) => {
    sections.push({
      title: (year - index).toString(),
      content: <ThumbnailGrid items={items} />,
    });
  });

  return (
    <div style={{marginBottom: '20px'}}>
      <Accordion sections={sections} />
      <p>{loading && 'loading...'}</p>
      <Button onClick={handleLoadMore}>Load more</Button>
    </div>
  );
}
